import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { filterAndSortContextVariables, getAllContextVariables } from './utils';
import { PROACTIVE_CONVERSATIONS_SYSTEM_CONTEXT_VARIABLES } from 'src/components/dashboard-pages/solve-insights/cards/systemContextVariablesOptions';
import useIsProactiveConversationsEnabled from 'src/pages/intent-conversation-analytics/hooks/useIsProactiveConversationsEnabled';
import { useGetContextVariablesQuery } from 'src/services/dashboard-api';
import { SystemContextVariableType } from 'src/services/insight-cards/types';
import { selectTicketFieldMappingsState } from 'src/slices/ticket-field-mappings/ticketFieldMappingsSlice';
import { ContextVariable } from 'src/types/actionBuilderApiTypes';

export const useGetContextVariables = ({
  filterFn,
  shouldIncludeCustomMetricsSystemContextVariables = false,
  shouldIncludeDynamicListContextVariables = true,
  shouldIncludeRuntimeSystemContextVariables = false,
  shouldIncludeSystemContextVariables = false,
  shouldIncludeTemplateContextVariables = false,
  shouldIncludeTriageContextVariables = false,
  shouldIncludeWorkflowTagContextVariable = true,
  shouldOnlyReturnSupportedFilterableContextVariables = false,
  systemContextVariableIdsToInclude,
}: {
  filterFn?: (cv: ContextVariable) => boolean;
  shouldIncludeCustomMetricsSystemContextVariables?: boolean;
  shouldIncludeDynamicListContextVariables?: boolean;
  shouldIncludeRuntimeSystemContextVariables?: boolean;
  shouldIncludeSystemContextVariables?: boolean;
  shouldIncludeTemplateContextVariables?: boolean;
  shouldIncludeTriageContextVariables?: boolean;
  shouldIncludeWorkflowTagContextVariable?: boolean;
  shouldOnlyReturnSupportedFilterableContextVariables?: boolean;
  systemContextVariableIdsToInclude?: string[];
} = {}) => {
  const ticketFieldMappingsState = useSelector(selectTicketFieldMappingsState);
  const isProactiveConversationsEnabled = useIsProactiveConversationsEnabled();
  const { data, isLoading } = useGetContextVariablesQuery();
  const {
    context_variables: contextVariablesData = [],
    runtime_system_context_variables: runtimeSystemContextVariables = [],
    template_context_variables: templateContextVariables = [],
    usable_system_context_variables: usableSystemContextVariables = [],
  } = data ?? {};
  const { ticketFieldMappings = [] } = ticketFieldMappingsState;

  return useMemo(() => {
    const contextVariables = getAllContextVariables({
      contextVariablesData,
      runtimeSystemContextVariables,
      shouldIncludeCustomMetricsSystemContextVariables,
      shouldIncludeRuntimeSystemContextVariables,
      shouldIncludeSystemContextVariables,
      shouldIncludeTemplateContextVariables,
      shouldIncludeTriageContextVariables,
      systemContextVariableIdsToInclude,
      templateContextVariables,
      ticketFieldMappings,
      usableSystemContextVariables,
    }).filter(
      // The proactive-conversations custom-metrics variables are only offered
      // to entitled orgs.
      cv =>
        isProactiveConversationsEnabled ||
        !cv.is_system_context_variable ||
        !PROACTIVE_CONVERSATIONS_SYSTEM_CONTEXT_VARIABLES.includes(
          cv.context_variable_id as SystemContextVariableType,
        ),
    );

    return {
      contextVariables: filterAndSortContextVariables(
        contextVariables,
        shouldIncludeWorkflowTagContextVariable,
        shouldIncludeDynamicListContextVariables,
        shouldOnlyReturnSupportedFilterableContextVariables,
        filterFn,
      ),
      isLoading,
    };
  }, [
    contextVariablesData,
    shouldIncludeCustomMetricsSystemContextVariables,
    shouldIncludeSystemContextVariables,
    shouldIncludeTemplateContextVariables,
    shouldIncludeTriageContextVariables,
    systemContextVariableIdsToInclude,
    templateContextVariables,
    ticketFieldMappings,
    usableSystemContextVariables,
    shouldIncludeWorkflowTagContextVariable,
    shouldIncludeDynamicListContextVariables,
    shouldOnlyReturnSupportedFilterableContextVariables,
    shouldIncludeRuntimeSystemContextVariables,
    runtimeSystemContextVariables,
    filterFn,
    isLoading,
    isProactiveConversationsEnabled,
  ]);
};
